import { SideData } from '@App/app/entities/layer/side-data.model';
import { Injectable } from '@angular/core';
import { Mesh, Plane, Vector3 } from 'babylonjs';
import { getCenter } from '../../../../helpers/rectangle-tools-helpers';
import {
  CompleteSideData,
  SidesCalculationResult,
  ThreeSidesResult,
} from './rectangle-tools-geometry.model';
import { RectangleToolsRaycastService } from './rectangle-tools-raycast.service';
import { RectangleToolsUtilsService } from './rectangle-tools-utls.service';

const DEFAULT_SIDE: SideData = { length: 0 };
const DEFAULT_PLANE = (): Plane => new Plane(1, 0, 0, 0);

@Injectable({
  providedIn: 'root',
})
export class RectangleToolsGeometryService {
  constructor(
    private _rectangleToolsUtilsService: RectangleToolsUtilsService,
    private _raycastService: RectangleToolsRaycastService,
  ) {}

  calculateSidesData(
    originalSurfacePoints: Mesh[] = [],
    adjacentSurfacePoints: Mesh[] = [],
    topBottomSurfacePoints: Mesh[] = [],
  ): SidesCalculationResult {
    const originalSurfacePlane = this._rectangleToolsUtilsService.createPlane(
      originalSurfacePoints,
      topBottomSurfacePoints.length ? topBottomSurfacePoints : adjacentSurfacePoints,
    );
    const originalSurfaceOppositePoints = this._raycastService.getOppositesPoints(
      originalSurfacePoints,
      originalSurfacePlane.normal.clone(),
    );
    const originalPositions = originalSurfacePoints.map((point) => point.position);
    const originalSurfaceDistance = Vector3.Distance(
      getCenter(originalSurfaceOppositePoints),
      getCenter(originalPositions),
    );
    const initialCenter = getCenter([...originalPositions, ...originalSurfaceOppositePoints]);

    const sidesResult = this.resolveThreeSides(
      topBottomSurfacePoints,
      adjacentSurfacePoints,
      originalSurfacePlane,
      initialCenter,
    );

    const { centerPosition, adjacentSurfacePlane, topPlane, topBottomSurfaceData } = sidesResult;
    const refined = this._raycastService.refineSideLengths(
      originalSurfacePlane,
      adjacentSurfacePlane,
      centerPosition,
      sidesResult.originalSurfaceData,
      originalSurfaceDistance,
    );

    return {
      originalSurfaceLength: refined.originalSurfaceData.length,
      adjacentSurfaceLength: refined.adjacentSurfaceData.length,
      topBottomSurfaceLength: topBottomSurfaceData.length,
      topPlaneNormal: topPlane.normal,
      originalSurfacePlaneNormal: originalSurfacePlane.normal,
      centerPosition,
    };
  }

  private resolveThreeSides(
    topBottomSurfacePoints: Mesh[],
    adjacentSurfacePoints: Mesh[],
    originalSurfacePlane: Plane,
    initialCenter: Vector3,
  ): ThreeSidesResult {
    if (topBottomSurfacePoints.length > 2) {
      const topPlane = this._rectangleToolsUtilsService.createPlane(topBottomSurfacePoints);
      const projectedPoint = initialCenter.projectOnPlane(
        topPlane,
        initialCenter.add(topPlane.normal),
      );
      return this.calculateThreeSides(
        originalSurfacePlane,
        initialCenter,
        topPlane,
        DEFAULT_PLANE(),
        projectedPoint,
        true,
      );
    }
    if (adjacentSurfacePoints.length > 2) {
      const adjacentPlane = this._rectangleToolsUtilsService.createPlane(adjacentSurfacePoints);
      const projectedPoint = initialCenter.projectOnPlane(
        adjacentPlane,
        initialCenter.add(adjacentPlane.normal),
      );
      return this.calculateThreeSides(
        originalSurfacePlane,
        initialCenter,
        DEFAULT_PLANE(),
        adjacentPlane,
        projectedPoint,
        false,
      );
    }
    return this.emptyResult(initialCenter);
  }

  private calculateThreeSides(
    originalSurfacePlane: Plane,
    centerPosition: Vector3,
    topPlane: Plane,
    adjacentSurfacePlane: Plane,
    projectedPoint: Vector3,
    isTopKnown: boolean,
  ): ThreeSidesResult {
    const derivedPlane = isTopKnown ? adjacentSurfacePlane : topPlane;
    const refinePlane = isTopKnown ? topPlane : adjacentSurfacePlane;
    const toResult = this.makeResultBuilder(
      topPlane,
      adjacentSurfacePlane,
      centerPosition,
      isTopKnown,
    );

    const originalData = this._raycastService.calculateSideData(
      originalSurfacePlane.normal.clone(),
      centerPosition,
    );
    if (!this.isComplete(originalData)) {
      return toResult(originalData);
    }

    derivedPlane.copyFromPoints(projectedPoint, originalData.pointB, originalData.pointA);
    const secondData = this._raycastService.calculateSideData(derivedPlane.normal, centerPosition);
    if (!this.isComplete(secondData)) {
      return toResult(originalData, secondData);
    }

    const { center, data: refinedData } = this.refineFromPair(
      refinePlane,
      secondData,
      originalData.pointA,
    );

    return toResult(originalData, secondData, refinedData, center);
  }

  private makeResultBuilder(
    topPlane: Plane,
    adjacentSurfacePlane: Plane,
    defaultCenter: Vector3,
    isTopKnown: boolean,
  ) {
    return (
      original: SideData = DEFAULT_SIDE,
      second: SideData = DEFAULT_SIDE,
      refined: SideData = DEFAULT_SIDE,
      center: Vector3 = defaultCenter,
    ): ThreeSidesResult => ({
      centerPosition: center,
      adjacentSurfacePlane,
      topPlane,
      originalSurfaceData: original,
      adjacentSurfaceData: isTopKnown ? second : refined,
      topBottomSurfaceData: isTopKnown ? refined : second,
    });
  }

  private isComplete(data: SideData): data is CompleteSideData {
    return !!data.pointA && !!data.pointB;
  }

  private refineFromPair(
    planeToRefine: Plane,
    knownData: CompleteSideData,
    thirdPoint: Vector3,
  ): { center: Vector3; data: SideData } {
    planeToRefine.copyFromPoints(knownData.pointA, knownData.pointB, thirdPoint);
    let center = Vector3.Center(knownData.pointA, knownData.pointB);
    const data = this._raycastService.calculateSideData(planeToRefine.normal, center);
    if (this.isComplete(data)) {
      center = Vector3.Center(data.pointA, data.pointB);
    }
    return { center, data };
  }

  private emptyResult(centerPosition: Vector3): ThreeSidesResult {
    return {
      centerPosition,
      adjacentSurfacePlane: DEFAULT_PLANE(),
      topPlane: DEFAULT_PLANE(),
      originalSurfaceData: DEFAULT_SIDE,
      adjacentSurfaceData: DEFAULT_SIDE,
      topBottomSurfaceData: DEFAULT_SIDE,
    };
  }
}
